import React, { useEffect, useState } from "react";
import CustomTable from "components/common/CustomTable";
import PageFrame from "components/layout/PageFrame";
import Button from "components/common/Button";
import TextInput from "components/common/InputField/TextInput";
import { debounce, truncate } from "lodash";
import Dropdown from "components/common/Dropdown";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { salesActions } from "redux/Ldb/actions";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import moment from "moment";
import { formatMoney, getCurrencyFromCurrencyCode } from "helpers";
import { Tooltip } from "react-tooltip";
import VendorAvatar from "components/common/VendorAvatar";
import CustomTableLoader from "components/common/CustomTableLoader";
import Loader from "components/common/Loader";
import { useNavigate } from "react-router-dom";

const SalesSummary = ({
    selectedOrders,
    setShowSummary,
    salesChannel,
}: {
    selectedOrders: Record<string, any>[];
    setShowSummary: (show: boolean) => void;
    salesChannel: string[];
}) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [channel, setChannel] = useState("");
    const [salesSummaryData, setSalesSummaryData] = useState<Record<string, any>[]>([]);
    const [originalSalesSummaryData, setOriginalSalesSummaryData] = useState([]);

    const { fetchingSalesSummary, fetchSalesSummarySuccess } = useAppSelector(
        (state) => state.sales
    );

    const tableHeader = [
        { title: "Item", widthClass: "w-[20%]" },
        { title: "Total ordered qty", widthClass: "w-[10]" },
        { title: "Current stock level", widthClass: "w-[13.5%]" },
        { title: "Sales channel", widthClass: "w-[20%]" },
        { title: "Estimated revenue", widthClass: "w-[20%]" },
        { title: "Status", widthClass: "w-[13.5%]" },
    ];

    useEffect(() => {
        if (selectedOrders?.length > 0) {
            const body = {
                saleOrderIds: selectedOrders?.map((order) => order?._id),
            };
            dispatch(salesActions.fetchSalesSummary(body));
        }
    }, [selectedOrders]);

    useEffect(() => {
        if (Boolean(fetchSalesSummarySuccess)) {
            setSalesSummaryData(fetchSalesSummarySuccess);
            setOriginalSalesSummaryData(fetchSalesSummarySuccess);
            dispatch(salesActions.resetFetchSalesSummarySuccess());
        }
    }, [dispatch, fetchSalesSummarySuccess]);

    useEffect(() => {
        let filteredData = [...originalSalesSummaryData]; // Always start from original

        if (search) {
            filteredData = filteredData.filter(
                (order) =>
                    order?.inventory?.productName?.toLowerCase()?.includes(search.toLowerCase()) ||
                    order?.inventory?.sku?.toLowerCase()?.includes(search.toLowerCase()) ||
                    order?.channels?.some((ch) => {
                        const channel = ch.toLowerCase();
                        const keyword = search.toLowerCase();
                        return (
                            ("retail".startsWith(keyword) && channel.includes("synth")) ||
                            channel.includes(keyword)
                        );
                    })
            );
        }

        if (channel) {
            filteredData = filteredData.filter((order) =>
                order?.channels?.some((ch) =>
                    channel.toLowerCase() === "retail"
                        ? ch?.toLowerCase()?.includes("synth")
                        : ch?.toLowerCase()?.includes(channel.toLowerCase())
                )
            );
        }

        setSalesSummaryData(filteredData);
    }, [search, channel, originalSalesSummaryData]);

    const tableBody = salesSummaryData?.map((order, idx) => {
        const isEnoughStock =
            order?.totalQuantity < order?.inventory?.productStockDetails?.stockLevel;
        return [
            {
                content: (
                    <div
                        className={`py-3  flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        <div className="flex items-center space-x-3">
                            {order?.inventory?.productImageDetails?.productAvatar ||
                            order?.inventory?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        order?.inventory?.productImageDetails?.productAvatar ||
                                        order?.inventory?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={order?.inventory?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <div>
                                    <a id={`product-column-${idx}`}>
                                        <span className="block text-sm">
                                            {truncate(order?.inventory?.productName, {
                                                length: 30,
                                            })}
                                        </span>
                                        <span className="text-slate-500 text-sm">
                                            {order?.inventory?.sku
                                                ? `SKU-${order?.inventory?.sku}`
                                                : ""}
                                        </span>
                                    </a>
                                    <Tooltip
                                        anchorSelect={`#product-column-${idx}`}
                                        place={"right"}
                                        content={order?.inventory?.productName}
                                        style={{
                                            maxWidth: "250px",
                                            zIndex: 9999,
                                            background: "#fff",
                                            borderRadius: "6px",
                                            border: "1px solid #E2E8F0",
                                            color: "#1E293B",
                                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ),
                widthClass: "!pl-0",
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 border-slate-100 text-sm font-rocGroteskMedium text-slate-900`}
                    >
                        {order?.totalQuantity?.toLocaleString()}
                    </div>
                ),
            },

            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full border-slate-100 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <span>
                            {order?.inventory?.productStockDetails?.stockLevel?.toLocaleString() ||
                                "0"}
                            {` `}
                            {order?.inventory?.productStockDetails?.unitOfMeasurement || ""}
                        </span>
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3  items-center h-full flex space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {order?.channels?.map((channel) => {
                            return (
                                <div className="flex items-center space-x-2 ">
                                    {channel?.toLowerCase()?.includes("synth")
                                        ? "Retail"
                                        : displayProductChannelIcon(channel, "!w-6 !h-6")}

                                    <span className="captalize ">
                                        {channel?.toLowerCase()?.includes("synth")
                                            ? ""
                                            : channel?.toLowerCase()?.includes("trackstar")
                                            ? channel?.split(".")[1]
                                            : channel}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        {getCurrencyFromCurrencyCode(order?.currency)}
                        {formatMoney().format(order?.estimatedRevenue?.toFixed(2))}
                    </div>
                ),
            },
            {
                content: (
                    <div
                        className={`py-3 pr-3 flex items-center h-full  space-x-2 text-sm font-rocGroteskMedium text-slate-900 `}
                    >
                        <div
                            className={`w-fit h-[21px] flex items-center text-sm py-1 px-2  ${
                                isEnoughStock ? "bg-g-25" : "bg-[#FCC34E26]"
                            } rounded-2xl`}
                        >
                            <span
                                className={`text-xs ${
                                    isEnoughStock ? "text-g-60" : "text-[#F4B000]"
                                }`}
                            >
                                {isEnoughStock ? "In stock" : "Out of stock risk"}
                            </span>
                        </div>
                    </div>
                ),
            },
        ];
    });

    return (
        <div className="bg-white rounded-lg border border-slate-100 font-rocGroteskMedium pb-12">
            <div className="flex items-center justify-between px-4 py-4 border-b border-n-20">
                <div
                    className="flex items-center space-x-1.5 w-fit cursor-pointer"
                    onClick={() => setShowSummary(false)}
                >
                    <i className="ri-arrow-left-line text-[20px]"></i>
                    <p className="text-[22px] text-gm-50 font-rocGroteskMedium">
                        Sales items summary
                    </p>
                </div>

                <div className="flex gap-2 items-center">
                    <Button
                        btnText="Close"
                        btnClassname="!px-8 !py-2.5 !w-fit !text-[13px] !rounded-[4px] !text-gm-50 !bg-n-20 !border-n-20"
                        onClick={() => {
                            setShowSummary(false);
                        }}
                    />
                    <Button
                        btnText="Create PO"
                        btnPermission={["all_access", "create_purchase_order"]}
                        btnClassname="!px-6 !py-2.5 !w-fit !text-[13px] !border-none !rounded-[4px] !text-gm-50"
                        onClick={() => {
                            navigate(`/dashboard/purchase/create-purchase-request`, {
                                state: { internalNavigation: true },
                            });
                        }}
                    />
                </div>
            </div>

            <div className="px-4 py-8">
                <div className="flex gap-2 items-center pb-8">
                    <TextInput
                        name={"search"}
                        value={search}
                        type={"text"}
                        inputPlaceholder={"Search products or orders"}
                        inputClassName={"!h-[38px] pl-[0px] !bg-white text-sm !mb-0"}
                        onChange={(e) => {
                            setSearch(e?.target?.value);
                        }}
                        leftIcon={
                            <i className="ri-search-line before:content-['\f0d1] text-slate-400 mt-[2px]"></i>
                        }
                        inputContainerClassname={
                            "!rounded-[4px]  max-sm:!w-full !pl-[40px] !h-[40px] !bg-white !border !border-n-20"
                        }
                    />
                    <Dropdown
                        handleChange={(name, value) => {
                            setChannel(value);
                        }}
                        dropdown={
                            <div className="flex items-center justify-between whitespace-nowrap h-[40px]  py-2 px-4 space-x-1 rounded-[5px] border border-slate-200">
                                <div className="flex items-center space-x-1">
                                    <span className={`text-g-75 font-rocGroteskMedium text-[13px]`}>
                                        {truncate(channel, { length: 16 }) || "Sales channel"}
                                    </span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    {Boolean(channel) && (
                                        <i
                                            onClick={() => {
                                                setChannel("");
                                            }}
                                            className="ri-close-circle-line text-slate-500"
                                        ></i>
                                    )}
                                    <i className="ri-arrow-down-s-line text-g-75"></i>
                                </div>
                            </div>
                        }
                        wholeContainerClass="!w-fit max-sm:!w-full"
                        id="modalContent"
                        dropdownContainerClasses={`shadow-cardShadow-4 !w-full border border-slate-100 right-0 rounded !max-h-fit !pt-0`}
                        dropdownOptions={
                            salesChannel?.length > 0
                                ? [
                                      ...salesChannel?.map((item) => {
                                          return {
                                              label: item,
                                              value: item,
                                          };
                                      }),
                                  ]
                                : []
                        }
                    />
                </div>
                {fetchingSalesSummary ? (
                    <CustomTableLoader tableHeader={tableHeader} />
                ) : salesSummaryData?.length > 0 ? (
                    <div>
                        <CustomTable
                            tableBody={tableBody}
                            tableHeader={tableHeader}
                            isCellBordered={true}
                            headerContainerClass="!bg-slate-50  !border-slate-100 "
                            bodyItemClass="hover:border-slate-100 !border-b hover:bg-slate-50 "
                            headerItemClass="font-rocGroteskMedium !text-slate-700"
                            tableContainerClass="pb-[90px] mb-[-90px]"
                            scrollWithin={false}
                        />
                    </div>
                ) : (
                    (search || channel) && (
                        <p className="text-slate-500 text-sm font-rocGroteskMedium text-center">
                            No data matches your search query
                        </p>
                    )
                )}
            </div>
        </div>
    );
};

export default SalesSummary;
